import axios, { AxiosResponse } from 'axios';
import TokenManager from '../utils/tokenManager';
import Logger from '../utils/logger';
import { setupApiInterceptors } from '../utils/apiInterceptor';
import { getApiBaseUrl } from '../utils/apiBaseUrl';

// 환경변수에서 API 기본 URL 가져오기
const API_BASE_URL = getApiBaseUrl();
const API_PREFIX = '/api'; // API 프록시 경로

// 로그인 요청 인터페이스
export interface LoginRequest {
  admin_id: string;
  admin_pw: string;
}

// 로그인 응답 인터페이스 (백엔드 실제 응답 구조)
export interface LoginResponse {
  token_type: string;
  access_token: string;
  refresh_token: string;
  data: {
    admin_idx: number;
    admin_id: string;
    admin_phone: string;
    admin_name: string;
  };
}

// 프론트엔드 내부 처리용 로그인 결과 인터페이스
export interface LoginResult {
  success: boolean;
  message: string;
  data?: LoginResponse;
}

// 토큰 refresh 응답 인터페이스 (백엔드 실제 응답 구조)
export interface RefreshResponse {
  token_type: string;
  access_token: string;
  refresh_token: string;
}

// 프론트엔드 내부 처리용 refresh 결과 인터페이스
export interface RefreshResult {
  success: boolean;
  message: string;
  data?: RefreshResponse;
}

// Axios 인스턴스 생성
const authApi = axios.create({
  baseURL: `${API_BASE_URL}${API_PREFIX}`,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 공통 인터셉터 설정
setupApiInterceptors(authApi);

// 인증 서비스 클래스
export class AuthService {
  /**
   * 관리자 로그인
   * @param loginData 로그인 요청 데이터
   * @returns 로그인 결과
   */
  static async login(loginData: LoginRequest): Promise<LoginResult> {
    Logger.info('AuthService.login 메서드 시작', loginData);
    try {
      Logger.info('API 호출 시작: POST /admin/login');
      const response: AxiosResponse<LoginResponse> = await authApi.post('/admin/login', loginData);
      Logger.info('API 응답 받음', { status: response.status, data: response.data });

      // 로그인 성공 시 토큰 저장
      if (response.data && response.data.access_token) {
        Logger.info('토큰 저장 시작', {
          access_token: response.data.access_token.substring(0, 20) + '...',
          refresh_token: response.data.refresh_token.substring(0, 20) + '...',
          admin_info: response.data.data
        });

        TokenManager.setAccessToken(response.data.access_token);
        TokenManager.setRefreshToken(response.data.refresh_token);
        TokenManager.setAdminInfo(response.data.data);

        // 토큰 저장 후 확인
        const savedToken = TokenManager.getAccessToken();
        const savedAdminInfo = TokenManager.getAdminInfo();
        Logger.info('토큰 저장 완료', {
          saved_token: savedToken ? savedToken.substring(0, 20) + '...' : null,
          saved_admin_info: savedAdminInfo
        });

        return {
          success: true,
          message: '로그인에 성공했습니다.',
          data: response.data
        };
      }

      return {
        success: false,
        message: '로그인에 실패했습니다.',
      };
    } catch (error: any) {
      console.error('로그인 API 오류:', error);

      // 서버 응답이 있는 경우
      if (error.response?.data) {
        return {
          success: false,
          message: error.response.data.message || '로그인에 실패했습니다.',
        };
      }

      // 네트워크 오류 등
      return {
        success: false,
        message: '네트워크 오류가 발생했습니다. 다시 시도해주세요.',
      };
    }
  }

  /**
   * 토큰 refresh
   * AuthStateManager에서만 호출되어야 함 (단일 진입점)
   * @returns refresh 결과
   */
  static async refreshToken(): Promise<RefreshResult> {
    try {
      const refreshToken = TokenManager.getRefreshToken();

      if (!refreshToken) {
        throw new Error('Refresh token이 없습니다.');
      }

      // refresh API는 인터셉터를 우회해야 하므로 직접 axios 사용
      // authApi를 사용하면 인터셉터에서 getValidToken()을 호출하여 순환 참조 발생 가능
      // 따라서 refresh API는 인터셉터 없이 직접 axios로 호출
      const response: AxiosResponse<RefreshResponse> = await axios.get(
        `${API_BASE_URL}${API_PREFIX}/admin/refresh`,
        {
          headers: {
            'Authorization': `Bearer ${refreshToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      // refresh 성공 시 새 토큰 저장
      if (response.data && response.data.access_token) {
        TokenManager.setAccessToken(response.data.access_token);
        if (response.data.refresh_token) {
          TokenManager.setRefreshToken(response.data.refresh_token);
        }

        return {
          success: true,
          message: '토큰 갱신에 성공했습니다.',
          data: response.data
        };
      }

      return {
        success: false,
        message: '토큰 갱신에 실패했습니다.',
      };
    } catch (error: any) {
      console.error('토큰 refresh API 오류:', error);

      // 서버 응답이 있는 경우
      if (error.response?.data) {
        const errorMessage = error.response.data.message || '토큰 갱신에 실패했습니다.';
        return {
          success: false,
          message: errorMessage,
        };
      }

      // 네트워크 오류 등
      return {
        success: false,
        message: '네트워크 오류가 발생했습니다. 다시 시도해주세요.',
      };
    }
  }

  /**
   * 로그아웃
   */
  static logout(): void {
    console.log('AuthService.logout 시작');

    // 모든 토큰 및 정보 삭제
    TokenManager.clearAll();

    console.log('AuthService.logout 완료 - 토큰 정리됨');

    // 로그인 페이지로 강제 리다이렉트
    // window.location.href를 사용하여 React Router를 우회하여 확실한 리다이렉트 보장
    window.location.href = '/login';
  }

  /**
   * 현재 로그인 상태 확인
   * @returns 로그인 여부
   */
  static isLoggedIn(): boolean {
    const accessToken = TokenManager.getAccessToken();
    const refreshToken = TokenManager.getRefreshToken();

    // access token이 유효한 경우
    if (accessToken && !TokenManager.isTokenExpired(accessToken)) {
      console.log('AuthService.isLoggedIn - Access token 유효:', {
        has_access_token: true,
        access_token_preview: accessToken.substring(0, 20) + '...',
        is_access_expired: false,
        result: true
      });
      return true;
    }

    // access token이 만료되었지만 refresh token이 유효한 경우
    if (refreshToken && !TokenManager.isTokenExpired(refreshToken)) {
      console.log('AuthService.isLoggedIn - Refresh token 유효 (갱신 가능):', {
        has_access_token: !!accessToken,
        has_refresh_token: true,
        refresh_token_preview: refreshToken.substring(0, 20) + '...',
        is_access_expired: accessToken ? TokenManager.isTokenExpired(accessToken) : true,
        is_refresh_expired: false,
        result: true
      });
      return true; // 갱신 가능한 상태
    }

    console.log('AuthService.isLoggedIn - 모든 토큰 무효:', {
      has_access_token: !!accessToken,
      has_refresh_token: !!refreshToken,
      is_access_expired: accessToken ? TokenManager.isTokenExpired(accessToken) : true,
      is_refresh_expired: refreshToken ? TokenManager.isTokenExpired(refreshToken) : true,
      result: false
    });

    return false;
  }

  /**
   * 저장된 관리자 정보 가져오기
   * @returns 관리자 정보 또는 null
   */
  static getAdminInfo(): any {
    return TokenManager.getAdminInfo();
  }
}

export default AuthService; 